/**
 * "Recomeçar com o @Atendente" (#24784): grava um marcador na conversa e, dali para
 * trás, nada mais entra como memória do @Atendente para quem pediu. Existe porque a
 * conversa com ele é única por pessoa — sem isto, uma resposta errada ficava no
 * histórico e contaminava as seguintes, sem saída (conversa 667419, 07/10/2026).
 *
 * Nada é apagado: as mensagens continuam na tela, na busca e na trilha.
 *
 * O item mora no menu e o diálogo fora dele — o Menu desmonta o dropdown ao fechar,
 * e levaria o diálogo junto.
 */
import { Button, Group, Menu, Modal, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { IconRefresh } from '@tabler/icons-react'
import { chatApi } from '@/api/chat'

export function useRecomecarAtendente(cid: number) {
  const [aberto, { open, close }] = useDisclosure(false)
  const qc = useQueryClient()
  const recomecar = useMutation({
    mutationFn: () => chatApi.recomecarAtendente(cid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversation-messages', cid] })
      qc.invalidateQueries({ queryKey: ['conversations'] })
      close()
      notifications.show({ color: 'teal', title: 'Conversa recomeçada', message: 'O @Atendente não usa mais o que veio antes.' })
    },
    onError: () =>
      notifications.show({ color: 'red', title: 'Erro', message: 'Não foi possível recomeçar a conversa.' }),
  })

  const item = (
    <Menu.Item leftSection={<IconRefresh size={16} />} onClick={open}>
      Recomeçar com o @Atendente
    </Menu.Item>
  )

  const dialogo = (
    <Modal opened={aberto} onClose={close} title="Recomeçar com o @Atendente" centered size="sm">
      <Text size="sm">
        O @Atendente deixa de usar o que foi dito antes como memória nas suas próximas perguntas.
        Nada é apagado: as mensagens continuam aqui e na trilha de auditoria.
      </Text>
      <Group justify="flex-end" mt="md" gap="sm">
        <Button variant="subtle" color="gray" onClick={close}>Cancelar</Button>
        <Button onClick={() => recomecar.mutate()} loading={recomecar.isPending}>Recomeçar</Button>
      </Group>
    </Modal>
  )

  return { item, dialogo }
}
